import { useEffect, useMemo, useState } from 'react';

import { OptionType } from '@evinced-private/ui-common';

import type { LabelChangeForUpdate } from 'src/apps/web-sdks/services/api/helpers/LabelChangeHelpers';
import type { IWebSdkTest } from 'src/apps/web-sdks/types/WebSdkDashboardServiceTypes';
import { markOptionsWithWarnings } from 'src/common/helpers/ConfigurationWarningsHelper';
import type { ConfigurationLabel } from 'src/common/types/ConfigurationTypes';

import { extractValueFromLabel } from '../../../helpers/filter-section-v2';
import { useConfiguredValues } from '../../../hooks/useConfiguredValues';
import { FILTER_TYPES, FilterType } from '../../../types/filter-section-v2/FilterConstants';
import { buildDisplayNameToKeyMap, extractLabelKey } from '../../shared/helpers';
import {
	extractLabelOptionsFromTestRows,
	transformCoreLabelsToOptions
} from '../helpers/UpdateTestsModalHelpers';

interface UseLabelsManagementProps {
	testRows: IWebSdkTest[];
	tenantConfiguredLabels: ConfigurationLabel[];
}

interface UseLabelsManagementReturn {
	selectedLabel: OptionType[];
	setSelectedLabel: (label: OptionType[]) => void;
	selectedNewValue: OptionType[];
	setSelectedNewValue: (value: OptionType[]) => void;
	changes: LabelChangeForUpdate[];
	labelOptions: OptionType[];
	valueOptions: OptionType[];
	removeChange: (index: number) => void;
	resetChanges: () => void;
}

export const useLabelsManagement = ({
	testRows,
	tenantConfiguredLabels
}: UseLabelsManagementProps): UseLabelsManagementReturn => {
	const [selectedLabel, setSelectedLabel] = useState<OptionType[]>([]);
	const [selectedNewValue, setSelectedNewValue] = useState<OptionType[]>([]);
	const [changes, setChanges] = useState<LabelChangeForUpdate[]>([]);
	const configuredValues = useConfiguredValues();

	const labelOptions = useMemo((): OptionType[] => {
		const options = extractLabelOptionsFromTestRows(testRows, tenantConfiguredLabels);

		const labelKeyToWarningConfig: Record<
			string,
			{
				configuredValues: Set<string>;
				filterType: FilterType;
				displayName: string;
			}
		> = {
			[FILTER_TYPES.ENVIRONMENT.toLowerCase()]: {
				configuredValues: configuredValues.environment,
				filterType: FILTER_TYPES.ENVIRONMENT,
				displayName: configuredValues.environmentDisplayName
			},
			[FILTER_TYPES.UNIT_ID.toLowerCase()]: {
				configuredValues: configuredValues.unitId,
				filterType: FILTER_TYPES.UNIT_ID,
				displayName: configuredValues.unitIdDisplayName
			}
		};

		return options.map((option) => {
			const labelKey = extractLabelKey(option.value as string);
			const warningConfig = labelKeyToWarningConfig[labelKey];

			if (warningConfig) {
				return markOptionsWithWarnings(
					[option],
					warningConfig.configuredValues,
					warningConfig.displayName,
					extractValueFromLabel
				)[0];
			}

			return option;
		});
	}, [testRows, tenantConfiguredLabels, configuredValues]);

	const valueOptions = useMemo((): OptionType[] => {
		const selectedLabelValue =
			selectedLabel.length > 0 ? (selectedLabel[0].value as string) : undefined;
		const options = transformCoreLabelsToOptions(tenantConfiguredLabels, selectedLabelValue);

		if (!selectedLabelValue) {
			return options;
		}

		const labelKey = extractLabelKey(selectedLabelValue);
		const labelKeyToWarningConfig: Record<
			string,
			{
				configuredValues: Set<string>;
				filterType: FilterType;
				displayName: string;
			}
		> = {
			[FILTER_TYPES.ENVIRONMENT.toLowerCase()]: {
				configuredValues: configuredValues.environment,
				filterType: FILTER_TYPES.ENVIRONMENT,
				displayName: configuredValues.environmentDisplayName
			},
			[FILTER_TYPES.UNIT_ID.toLowerCase()]: {
				configuredValues: configuredValues.unitId,
				filterType: FILTER_TYPES.UNIT_ID,
				displayName: configuredValues.unitIdDisplayName
			}
		};

		const warningConfig = labelKeyToWarningConfig[labelKey];
		if (warningConfig) {
			return markOptionsWithWarnings(
				options,
				warningConfig.configuredValues,
				warningConfig.displayName,
				extractValueFromLabel
			);
		}

		return options;
	}, [tenantConfiguredLabels, selectedLabel, configuredValues]);

	useEffect(() => {
		if (selectedLabel.length > 0 && selectedNewValue.length > 0) {
			const oldLabelValue = selectedLabel[0].value as string;
			const newLabelValue = selectedNewValue[0].value as string;

			const displayNameMap = buildDisplayNameToKeyMap(tenantConfiguredLabels);
			const normalizedKey = extractLabelKey(oldLabelValue, displayNameMap);

			const newChange: LabelChangeForUpdate = {
				oldLabelValue,
				newLabelValue: `${normalizedKey}:${newLabelValue}`
			};

			setChanges((prevChanges) => {
				const existingIndex = prevChanges.findIndex(
					(change) => change.oldLabelValue === newChange.oldLabelValue
				);
				if (existingIndex >= 0) {
					const updated = [...prevChanges];
					updated[existingIndex] = newChange;
					return updated;
				}
				return [...prevChanges, newChange];
			});

			setSelectedLabel([]);
			setSelectedNewValue([]);
		}
	}, [selectedLabel, selectedNewValue, tenantConfiguredLabels]);

	const removeChange = (index: number): void => {
		setChanges((prev) => prev.filter((_, i) => i !== index));
	};

	const resetChanges = (): void => {
		setChanges([]);
		setSelectedLabel([]);
		setSelectedNewValue([]);
	};

	return {
		selectedLabel,
		setSelectedLabel,
		selectedNewValue,
		setSelectedNewValue,
		changes,
		labelOptions,
		valueOptions,
		removeChange,
		resetChanges
	};
};
